import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useServerError} from "../../../../hooks/useServerError";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import {toast} from "react-toastify";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import PostCategoryForm from "./PostCategoryForm";

const initialState = {
    id: 0,
    name: '',
    postCategoryDescription: '',
    isBlocked: false,
    showInMain: false,
    hasHeader: false,
    hasFooter: false,
    sendPush: false,
    headMenu: false,
    postType: 0,
    listType: 0,
    menu: 0,
    icon: null,
    iconName: '',
    iconSize: null,
    targets: null
}

const PostCategory = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [postCategory, setPostCategory] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setPostCategory(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/postCategory/getPostCategory?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {
                            setPostCategory(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'postCategory',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'postCategory',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        }
    }

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/postCategory/save`, {
                id: postCategory.id,
                name: values.name,
                postCategoryDescription: values.postCategoryDescription,
                isBlocked: values.isBlocked,
                showInMain: values.showInMain,
                hasHeader: values.hasHeader,
                hasFooter: values.hasFooter,
                sendPush: values.sendPush,
                headMenu: values.headMenu,
                postType: values.postType,
                listType: values.listType,
                menu: values.menu,
                icon: values.image.image,
                iconSize: values.image.size,
                iconName: values.image.name,
                targets: values.targets
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("postCategory.form.saved-label"));

                        if (response.data.description) {
                            if (!id || id <= 0) {
                                await load(+response.data.description);
                            }
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'postCategory',
                            resultCode: response.data.resultCode,
                            description: response.data.resultCode === ResultCode.ServerError ? null : response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'postCategory',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    }

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("postCategory.form.form-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <Button buttonType={ButtonType.Default}
                                    form='post-category-form'
                                    type='submit'
                                    disabled={isLoading}>
                                {t("btn.save")}
                            </Button>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <PostCategoryForm {...postCategory} onSubmit={save}/>
            </div>
        </FormLayout>
    );
};

export default PostCategory;